import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { Clock, Building2, Users, Link2 } from 'lucide-react'
import Paper from '@/components/Paper'
import { formatFromUTC } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Ticket } from '../../../types/response'

interface InfoRowProps {
    label: string
    value: React.ReactNode
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
    <div className="py-1.5">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-gray-900 truncate">{value || '-'}</p>
    </div>
)

interface SectionProps {
    title: string
    icon: React.ReactNode
    children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ title, icon, children }) => (
    <div className="border-b border-gray-100 last:border-b-0 pb-3 mb-3 last:pb-0 last:mb-0">
        <div className="flex items-center gap-2 mb-2">
            <span className="text-gray-400">{icon}</span>
            <Typogaphy className="text-xs font-semibold text-gray-600 uppercase">
                {title}
            </Typogaphy>
        </div>
        {children}
    </div>
)

interface TicketInfoSidebarProps {
    ticket: Ticket
    onNavigateToLinkedTicket: (ticketId: string) => void
}

const TicketInfoSidebar: React.FC<TicketInfoSidebarProps> = ({
    ticket,
    onNavigateToLinkedTicket,
}) => {
    const { t } = useTranslation('ticket')

    const hasLinkedTickets =
        Array.isArray(ticket?.ticket_groups) && ticket.ticket_groups.length > 0

    return (
        <Paper className="p-3">
            {/* Timeline Section */}
            <Section
                title={t('timeline')}
                icon={<Clock className="w-3.5 h-3.5" />}
            >
                <div className="space-y-0.5">
                    <InfoRow
                        label={t('customer-request')}
                        value={formatFromUTC(ticket?.ticket_requested_at || '')}
                    />
                    <InfoRow
                        label={t('ticket-time-repply')}
                        value={formatFromUTC(ticket?.ticket_time_repply || '')}
                    />
                    {ticket?.ticket_time_response && (
                        <InfoRow
                            label={t('ticket-time-response')}
                            value={formatFromUTC(ticket.ticket_time_response)}
                        />
                    )}
                    <InfoRow
                        label={t('ticket-estimated-completion-date')}
                        value={formatFromUTC(
                            ticket?.ticket_estimated_completion_date || ''
                        )}
                    />
                    {ticket?.ticket_time_release && (
                        <InfoRow
                            label={t('ticket-date-completed')}
                            value={formatFromUTC(ticket.ticket_time_release)}
                        />
                    )}
                </div>
            </Section>

            {/* People Section */}
            <Section
                title={t('people')}
                icon={<Users className="w-3.5 h-3.5" />}
            >
                <div className="space-y-0.5">
                    <InfoRow
                        label={t('employee-name')}
                        value={ticket?.employee_name}
                    />
                    <InfoRow
                        label={t('ticket-technical-assignee')}
                        value={
                            ticket?.ticket_technical_assignee_names
                                ?.map((item) => item.employee_name)
                                .join(', ') || '-'
                        }
                    />
                </div>
            </Section>

            {/* Customer & Service Section */}
            <Section
                title={t('customer-service')}
                icon={<Building2 className="w-3.5 h-3.5" />}
            >
                <div className="space-y-0.5">
                    <div className="py-1.5">
                        <p className="text-xs text-gray-500">
                            {t('customers')}
                        </p>
                        {ticket?.customer_id ? (
                            <Link
                                to={generatePathDatacentral(
                                    paths.customerDetail,
                                    {
                                        id: ticket.customer_id,
                                    }
                                )}
                                className="text-sm text-primary hover:underline truncate block"
                            >
                                {ticket?.company_name || ticket?.customer_name}
                            </Link>
                        ) : (
                            <p className="text-sm text-gray-900 truncate">
                                {ticket?.customer_name || '-'}
                            </p>
                        )}
                    </div>
                    {ticket?.service_id && ticket.service_id > 1 && (
                        <div className="py-1.5">
                            <p className="text-xs text-gray-500">
                                {t('ticket.services')}
                            </p>
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    {
                                        id: ticket.service_id,
                                    }
                                )}
                                className="text-sm text-primary hover:underline truncate block"
                            >
                                {ticket?.product_group_name} (#
                                {ticket.service_id})
                            </Link>
                        </div>
                    )}
                </div>
            </Section>

            {/* Linked Tickets */}
            {hasLinkedTickets && (
                <Section
                    title={t('ticket-group')}
                    icon={<Link2 className="w-3.5 h-3.5" />}
                >
                    <div className="flex flex-wrap gap-1.5">
                        {ticket?.ticket_groups?.map((item: string) => (
                            <span
                                key={item}
                                onClick={() => onNavigateToLinkedTicket(item)}
                                className="cursor-pointer"
                            >
                                <Chip
                                    label={`#${item}`}
                                    size="small"
                                    className="hover:bg-gray-200 text-xs"
                                />
                            </span>
                        ))}
                    </div>
                </Section>
            )}
        </Paper>
    )
}

export default TicketInfoSidebar
